import { UserLocation } from '@/data/users/User';
import styled from '@emotion/styled';
import {
  Box,
  Divider,
  Fade,
  FormHelperText,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
} from '@mui/material';
import { EditTemplate, Switch } from 'klayowebshared';
import { debounce } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import React, { Component, SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import { Utils } from '../../common/Utils';
import { ErrorModel } from '../../common/models/ErrorModel';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { PaperOrDialog } from '../../components/PaperOrDialog';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { TextField } from '../../components/TextField';
import { TooltipIcon } from '../../components/TooltipIcon';
import { Dialog } from '../../components/dialogs/Dialog';
import { JobSelector } from '../../components/selectors/JobSelector';
import { LocationSelector } from '../../components/selectors/LocationSelector';
import { AttributeTable } from '../../components/table/AttributeTable';
import { ProfileTrainingTable } from '../../components/table/ProfileTrainingTable';
import {
  INTEGRATION_STATUS,
  IntegrationSource,
  PAGE_SIZE_FOR_ADD_EDIT,
  Training_Dialog_Mode,
} from '../../constants';
import { Data } from '../../data/Data';
import { AbstractAttribute } from '../../data/attributes/AbstractAttribute';
import { SettingsAttribute } from '../../data/settings/SettingsAttribute';
import {
  EmployeeJob,
  EmployeeTraining,
  IntegrationUser,
  SettingsEmployee,
  SettingsEmployeeData,
  DirectoryField,
} from '../../data/settings/SettingsEmployee';
import { SettingsJobList } from '../../data/settings/SettingsJobList';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import AddIcon from '../../resources/images/icons-add.svg';
import LeftIcon from '../../resources/images/icons-arrow-left.svg';
import RightIcon from '../../resources/images/icons-arrow-right.svg';
import AttributesIcon from '../../resources/images/icons-attribute.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import type {
  AddEditEmployeeViewProps,
  AddEditEmployeeViewState,
  PositionManager,
  PositionVacancy,
  RequirementType,
} from '../../types/models/settings.types';
import AddEditTrainingDialog from './AddEditTrainingDialog';
import { AssignManagerDialog } from './AssignManagerDialog';
import { AttributeSelectorDialog } from './AttributeSelectorDialog';
import EditAttributeDialog from './EditAttributeDialog';
import { MultiStudentIdTextField } from './MultiStudentIdTextField';
import { SelectVacancyDialog } from './SelectVacancyDialog';

class AddEditEmployeeViewBase extends Component<
  AddEditEmployeeViewProps & WithLocaleProps,
  AddEditEmployeeViewState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  existingEmployee: SettingsEmployee;

  constructor(props: AddEditEmployeeViewProps & WithLocaleProps) {
    super(props);
    this.onJobInputChange = debounce(this.onJobInputChange.bind(this), 300);
    this.state = {
      employee: props.employee ?? null,
      employeeLoaded: !props.editMode,
      error: props.error ?? null,
      step: 0,
      showSelectVacancyDialog: false,
      selectedPositionVacancies: null,
      selectedVacancy: null,
      createNewVacancy: false,
      search: null,
      searchTraining: null,
      showAttributeSelectDialog: false,
      showAssignManagerDialog: false,
      actionTarget: null,
      actionAttribute: null,
      actionTraining: null,
      editAttribute: null,
      editTraining: null,
      dobValid: true,
      startDateValid: true,
      hasEdit: false,
      saving: false,
      jobs: null,
      sameIdEnable: false,
      filteredAttributes: null,
      filteredTrainings: null,
      showAddEditTrainingDialog: false,
      isSavingEmployee: false,
      showLimitedUpgradeDialog: false,
      limitedUpgradeDialogMessage: '',
    };

    this.existingEmployee = props.editMode
      ? new SettingsEmployee(props.employee as any)
      : new SettingsEmployee();
  }

  componentDidMount(): void {
    const { integration, onLoadIntegration } = this.props;

    this.context?.eventBus?.on('saveEmployee', (isLoading: boolean) => {
      this.setState({ isSavingEmployee: isLoading });
    });

    this.loadDetails();
    this.loadJobs();

    if (!integration) onLoadIntegration?.();
  }

  componentDidUpdate(
    prevProps: AddEditEmployeeViewProps & WithLocaleProps,
    prevState: AddEditEmployeeViewState
  ): void {
    if (this.props.error !== prevProps.error) this.setState({ error: this.props.error ?? null });
    if (this.state.employee?.integrationUsers !== prevState.employee?.integrationUsers)
      this.loadSameIdStatus();
    if (prevProps.jobs !== this.props.jobs) this.forceUpdate();
    if (prevProps.managers !== this.props.managers) this.forceUpdate();
  }

  loadDetails(): void {
    const { employee } = this.state;
    const employeeId = employee ? employee.employeeId : this.props.employeeId;

    if (!employeeId) return;

    this.context?.setLoading?.('loading', true);

    Data.get<{ employee: SettingsEmployeeData }>(this.context, '/Employee/' + employeeId)
      .then((response) => {
        this.existingEmployee = new SettingsEmployee(response.data.employee);
        const employee = new SettingsEmployee(response.data.employee);

        if (response.data.employee?.job && response.data.employee.job.manager) {
          employee.setManager(response.data.employee.job.manager);
          this.existingEmployee.setManager(response.data.employee.job.manager);
        }

        this.setState({ employee, employeeLoaded: true });
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => {
        this.context?.setLoading?.('loading', false);
      });
  }

  /** A field set in Klayo for this person: what the directory says, and the way back to it. */
  fieldName(field: DirectoryField): string {
    return this.props.translate(
      field === DirectoryField.JobTitle
        ? 'labels.job'
        : field === DirectoryField.Location
          ? 'labels.location'
          : field === DirectoryField.Manager
            ? 'addEditEmployee.reportsTo'
            : 'employees.department'
    );
  }

  renderDirectoryOverride(field: DirectoryField): React.ReactNode {
    const { translate } = this.props;
    if (this.state.returnedToDirectory?.includes(field))
      return (
        <FormHelperText component='div' role='status'>
          {translate('employees.returnedToDirectory', { field: this.fieldName(field) })}
        </FormHelperText>
      );
    const fieldOverride = this.state.employee?.directoryOverrides?.find((o) => o.field === field);
    if (!fieldOverride) return null;
    const name = this.fieldName(field);
    return (
      <FormHelperText component='div'>
        {fieldOverride.directoryValue
          ? translate('employees.setInKlayoDirectorySays', {
              field: name,
              value: fieldOverride.directoryValue,
            })
          : translate('employees.setInKlayo', { field: name })}{' '}
        <Button
          size='sm'
          variant='text'
          label={translate('employees.returnToDirectory')}
          ariaLabel={translate('employees.returnFieldToDirectory', { field: name })}
          disabled={this.state.saving}
          onClick={() => this.onReturnToDirectory(field)}
        />
      </FormHelperText>
    );
  }

  /**
   * Removes the override: the directory's value comes back at its next scheduled run. The field goes back to its
   * saved value, so a pending edit to it can't set it in Klayo again on Save.
   */
  onReturnToDirectory(field: DirectoryField): void {
    const employeeId = this.state.employee?.employeeId;
    if (!employeeId) return;
    this.setState({ saving: true });
    Data.getAccessToken(this.context)
      .then(() =>
        Data.call('delete', `/Employee/${employeeId}/DirectoryOverride/${DirectoryField[field]}`)
      )
      .then(() => {
        const current = this.state.employee;
        if (!current) return;
        const employee = cloneDeep(current);
        employee.directoryOverrides = current.directoryOverrides.filter((o) => o.field !== field);
        if (field === DirectoryField.JobTitle) employee.job = this.existingEmployee?.job;
        if (field === DirectoryField.Location) {
          employee.locationId = this.existingEmployee?.locationId;
          employee.location = this.existingEmployee?.location;
        }
        if (field === DirectoryField.Manager) {
          employee.manager = this.existingEmployee?.manager;
          employee.reportsTo = this.existingEmployee?.reportsTo;
        }
        this.setState({
          employee,
          returnedToDirectory: [...(this.state.returnedToDirectory ?? []), field],
        });
      })
      .catch((e: unknown) => this.setState({ error: ErrorModel.parseServerError(e) }))
      .finally(() => this.setState({ saving: false }));
  }

  loadJobs(searchText?: string): void {
    this.context?.setLoading?.('jobs', true);

    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    // get setting job data then set to jobs state
    SettingsJobList.getWithPaging(this.context, pagingOptions)
      .then((jobs: unknown) => {
        this.setState({ jobs: jobs as SettingsJobList | null });
      })
      .catch((_error: unknown) => {})
      .finally(() => this.context?.setLoading?.('jobs', false));
  }

  loadSameIdStatus(): void {
    const { employee } = this.state;

    if (employee && employee.integrationUsers && employee.integrationUsers.length > 0) {
      const studentForAllIntegration = employee.integrationUsers.some(
        (s: IntegrationUser) => s.isForAllIntegrations === true
      );

      if (studentForAllIntegration) {
        this.setState({ sameIdEnable: true });
        return;
      }
    }

    this.setState({ sameIdEnable: false });
  }

  isValid(employee: SettingsEmployee | null, step: number): boolean {
    if (
      !employee ||
      !this.isFirstNameValid(employee) ||
      !this.isLastNameValid(employee) ||
      !this.isEmailValid(employee) ||
      !this.isDobValid(employee) ||
      !this.isStartDateValid(employee) ||
      !this.isStudentIdValid(employee)
    )
      return false;
    if (step === 0) return true;
    return this.isAttributesValid(employee);
  }

  isFirstNameValid(employee: SettingsEmployee | null): boolean {
    return (employee && employee.firstName && employee.firstName.length >= 1) === true;
  }

  isLastNameValid(employee: SettingsEmployee | null): boolean {
    return (employee && employee.lastName && employee.lastName.length >= 1) === true;
  }

  isEmailValid(employee: SettingsEmployee | null): boolean {
    return (employee && employee.email && Utils.isValidEmail(employee.email)) === true;
  }

  isDobValid(_employee: SettingsEmployee | null): boolean {
    return this.state.dobValid;
  }

  isStartDateValid(_employee: SettingsEmployee | null): boolean {
    return this.state.startDateValid;
  }

  isAttributesValid(employee: SettingsEmployee | null): boolean {
    if (!employee || !employee.attributes) return true;

    /*employee.attributes.forEach(a => {
        });*/

    return true;
  }

  isStudentIdValid(employee: SettingsEmployee | null): boolean {
    const { sameIdEnable } = this.state;

    if (employee && typeof employee.integrationUsers !== 'undefined') {
      if (employee.integrationUsers?.length < 0) return true;

      // check require for user sameId
      if (sameIdEnable) {
        if (Array.isArray(employee.integrationUsers) && employee.integrationUsers?.length > 0) {
          const userNameLength = employee.integrationUsers[0]?.username?.length ?? 0;

          return userNameLength > 0 && userNameLength <= 200;
        }
      }

      // if (Array.isArray(employee.integrationUsers) && employee?.integrationUsers?.length > 0) {
      //     return employee?.integrationUsers[0]?.username?.length <= 200;
      // }
    }

    return true;
  }

  hasChange(): boolean {
    const { employee } = this.state;

    if (employee && this.existingEmployee && this.existingEmployee.name !== employee.name)
      return true;

    return false;
  }

  onGetActiveIntegrationId(orgSetting: {
    organizationIntegrations?: Array<{ id: string; status: number }>;
  }): string | undefined {
    if (
      orgSetting &&
      orgSetting?.organizationIntegrations &&
      orgSetting.organizationIntegrations.length > 0
    ) {
      const activeIntegration = orgSetting?.organizationIntegrations.filter(
        (integration: { id: string; status: number }) =>
          integration.status === INTEGRATION_STATUS.ACTIVE
      );
      return activeIntegration[0]?.id;
    }
  }

  onSameIdEnable(e: React.ChangeEvent<HTMLInputElement>): void {
    const { integration } = this.props;
    const { employee } = this.state;
    if (!employee) return;
    const integrationUsers = cloneDeep(employee.integrationUsers);

    // If toggling ON (checked), keep existing logic
    if (e.target.checked) {
      if (integrationUsers && integrationUsers[0]) {
        integrationUsers[0].isForAllIntegrations = true;
        employee.integrationUsers = [integrationUsers[0]];
      } else {
        let integrationId = '';
        if (integration?.integrations && integration.integrations.length > 0) {
          integrationId = integration.integrations[0].id;
        }
        employee.integrationUsers = [
          {
            username: '',
            organizationIntegrationId: integrationId,
            isForAllIntegrations: true,
          },
        ];
      }
      this.setState({ sameIdEnable: true });
      return;
    }

    // If toggling OFF (unchecked), copy the single student ID to all integrations
    if (!e.target.checked) {
      // Get the current single student ID value (if any)
      let singleStudentId = '';
      if (integrationUsers && integrationUsers[0] && integrationUsers[0].isForAllIntegrations) {
        singleStudentId = integrationUsers[0].username || '';
      }
      // Build integrationUsers for each integration
      let newIntegrationUsers: IntegrationUser[] = [];
      if (integration?.integrations && integration.integrations.length > 0) {
        newIntegrationUsers = integration.integrations
          .filter(
            (item: { id: string; integrationSource?: number }) =>
              item.integrationSource !== IntegrationSource.Manual
          ) // Exclude Manual if needed
          .map((item: { id: string; integrationSource?: number }) => ({
            username: singleStudentId,
            organizationIntegrationId: item.id,
            isForAllIntegrations: false,
          }));
      }
      employee.integrationUsers = newIntegrationUsers;
      this.setState({ sameIdEnable: false });
      return;
    }
  }

  onFirstNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.updateName(e.target.value, employee.lastName);
    this.setState({ employee });
  }

  onLastNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.updateName(employee.firstName, e.target.value);
    this.setState({ employee });
  }

  onMiddleNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.middleName = e.target.value;
    this.setState({ employee });
  }

  onPreferredNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.preferredName = e.target.value;
    this.setState({ employee });
  }

  onDOBChange(date?: Date | null, valid?: boolean): void {
    const { employee } = this.state;
    if (!employee) return;
    /*if (valid)*/ employee.dateOfBirth = date;

    this.setState({ employee, dobValid: !!valid });
  }

  onStartDateChange(date?: Date | null, valid?: boolean): void {
    const { employee } = this.state;
    if (!employee) return;
    /*if (valid)*/ employee.startDate = date;

    this.setState({ employee, startDateValid: !!valid });
  }

  onEmailChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.email = e.target.value;
    this.setState({ employee });
  }

  onEmployeeIdChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.employeeNumber = e.target.value;
    this.setState({ employee });
  }

  onStudentIdChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
    integrationID?: string
  ): void {
    const { employee, sameIdEnable } = this.state;
    if (!employee) return;
    const integrationUsers = cloneDeep(employee.integrationUsers);

    const studentId = e.target.value;

    // same student id
    if (sameIdEnable && (studentId || studentId === '')) {
      const studentData = {
        username: studentId.trim() !== '' ? studentId.trim() : '',
        organizationIntegrationId: integrationID,
        isForAllIntegrations: true,
      };

      if (integrationUsers && integrationUsers.length > 0) {
        // edit if has user
        integrationUsers[0].username = studentData.username;
        employee.integrationUsers = [...integrationUsers];
      } else {
        // add new if integrationUsers empty
        employee.integrationUsers = [studentData];
      }

      this.setState({ employee });
      return;
    }

    // multi student id
    if (studentId && studentId.trim() !== '' && integrationUsers) {
      //get integrationUsers need to change
      const editUserId = integrationUsers.findIndex(
        (i: IntegrationUser) => i.organizationIntegrationId === integrationID
      );

      // new change data
      const studentData = {
        username: studentId.trim(),
        organizationIntegrationId: integrationID,
        isForAllIntegrations: false,
      };

      // if no student change exist, add new
      if ((!editUserId && editUserId !== 0) || editUserId === -1) {
        employee.integrationUsers = [...integrationUsers, studentData];
        return;
      }

      // edit if student exist
      // userIdForIntegrationExist.username = studentData.username;
      integrationUsers[editUserId].username = studentData.username;
      employee.integrationUsers = [...integrationUsers];
    }

    // remove empty student id in multi student id mode
    if ((studentId === '' || studentId.trim() === '') && integrationUsers) {
      const integrationUsersNotEmpty = integrationUsers.filter(
        (i: IntegrationUser) => i.organizationIntegrationId !== integrationID
      );

      // remove the student id with empty username;
      if (integrationUsersNotEmpty) {
        employee.integrationUsers = [...integrationUsersNotEmpty];
      }
    }

    this.setState({ employee });
  }

  onLocationsChange(
    _e: SyntheticEvent,
    location: UserLocation | NonNullable<UserLocation> | UserLocation[] | null
  ): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.locationId = location ? (location as UserLocation).locationId : undefined;
    // Edited again after returning it: saving now sets it in Klayo again, so the "returned" note no longer holds.
    const returnedToDirectory = this.state.returnedToDirectory?.filter(
      (field) => field !== DirectoryField.Location
    );
    this.setState({ employee, returnedToDirectory });
  }

  onJobInputChange(value: string): void {
    this.loadJobs(value);
  }

  onJobChange(_e: SyntheticEvent, position: EmployeeJob | null): void {
    const { jobs } = this.state;
    const { employee } = this.state;
    if (!employee) return;

    if (employee.job)
      (employee as SettingsEmployee & { prevJob?: EmployeeJob }).prevJob = employee.job;
    employee.job = position ?? undefined;
    if (this.state.returnedToDirectory?.includes(DirectoryField.JobTitle))
      this.setState({
        returnedToDirectory: this.state.returnedToDirectory.filter(
          (field) => field !== DirectoryField.JobTitle
        ),
      });
    let positions: PositionVacancy[] = [];
    if (position && jobs?.positions) {
      positions = (jobs.positions as PositionVacancy[]).filter(
        (p: PositionVacancy) => p.job?.jobDefinitionId === position.jobDefinitionId && p.isVacancy
      );
      if (employee.job) employee.job.jobId = undefined; //remove if trouble
    } else {
      employee.manager = undefined;
    }

    this.setState({
      showSelectVacancyDialog: positions.length !== 0,
      employee,
      createNewVacancy: positions.length === 0,
      selectedVacancy: null,
      selectedPositionVacancies: positions,
    });
  }

  onSearch(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const searchValue = e.target.value.toLowerCase();

    this.setState({ search: searchValue }, () => {
      if (this.state.employee && this.state.employee.attributes) {
        const filteredAttributes = this.state.employee.attributes.filter(
          (attr: SettingsAttribute) => attr.name?.toLowerCase().includes(searchValue)
        );

        this.setState({ filteredAttributes });
      }
    });
  }
  onSearchTrainings(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const searchValue = e.target.value.toLowerCase();

    this.setState({ searchTraining: searchValue }, () => {
      if (this.state.employee && this.state.employee.trainings) {
        const filteredTrainings = this.state.employee.trainings.filter(
          (training: EmployeeTraining) => training.name?.toLowerCase().includes(searchValue)
        );
        this.setState({ filteredTrainings });
      }
    });
  }

  onShowAttributeSelectDialog(_e: SyntheticEvent): void {
    if (!this.state.employee?.isFullAccess) {
      this.setState({
        showLimitedUpgradeDialog: true,
        limitedUpgradeDialogMessage: 'addEditEmployee.limitedCompetenciesMessage',
      });
      return;
    }
    this.setState({ showAttributeSelectDialog: true });
  }
  onShowAddEditTrainingDialog(_e: SyntheticEvent): void {
    if (!this.state.employee?.isFullAccess) {
      this.setState({
        showLimitedUpgradeDialog: true,
        limitedUpgradeDialogMessage: 'addEditEmployee.limitedTrainingMessage',
      });
      return;
    }
    this.setState({ showAddEditTrainingDialog: true });
  }

  onRemoveAttribute(_e: SyntheticEvent): void {
    const { employee, actionAttribute } = this.state;
    if (!employee || !actionAttribute) return;

    employee.attributes = employee.attributes.filter(
      (a: SettingsAttribute) =>
        !(a.attributeId === actionAttribute.attributeId && !a.isChild) &&
        a.parentAttributeId !== actionAttribute.attributeId
    );
    this.setState({ employee, actionTarget: null, actionAttribute: null });
  }

  onRemoveTraining(): void {
    const { employee, actionTraining } = this.state;
    if (!employee || !actionTraining) return;

    employee.trainings = employee.trainings.filter(
      (t: EmployeeTraining) => t.id !== actionTraining.id
    );

    this.setState({ employee, actionTarget: null, actionTraining: null });
  }

  onAttributeAction(e: SyntheticEvent, attribute: SettingsAttribute): void {
    const clonedAttr = Object.assign(
      Object.create(Object.getPrototypeOf(attribute)),
      cloneDeep(attribute)
    );
    this.setState({ actionTarget: e.target as HTMLElement, actionAttribute: clonedAttr });
  }

  onTrainingAction(e: SyntheticEvent, training: EmployeeTraining): void {
    this.setState({ actionTarget: e.target as HTMLElement, actionTraining: training });
  }

  onAttributeSelect(_e: SyntheticEvent, attribute: SettingsAttribute): void {
    if (!attribute.isChild && !attribute.isParent) {
      const clonedAttr = Object.assign(
        Object.create(Object.getPrototypeOf(attribute)),
        cloneDeep(attribute)
      );
      this.setState({ editAttribute: clonedAttr });
    }
  }

  onTrainingSelect(_e: SyntheticEvent, training: EmployeeTraining): void {
    if (!training.hasActions) return;

    this.setState({ editTraining: training, showAddEditTrainingDialog: true });
  }

  onCloseActionMenu(): void {
    this.setState({ actionTarget: null, actionAttribute: null });
  }

  onEditAttribute(): void {
    const { actionAttribute } = this.state;
    if (!actionAttribute) return;

    this.setState({
      actionTarget: null,
      actionAttribute: null,
      editAttribute: actionAttribute,
    });
  }
  onEditTraining(): void {
    const { actionTraining } = this.state;
    this.setState({
      actionTarget: null,
      actionTraining: null,
      editTraining: actionTraining,
      showAddEditTrainingDialog: true,
    });
  }

  onSaveAttribute(_e: SyntheticEvent, changedAttr: SettingsAttribute): void {
    const { employee } = this.state;

    if (!employee) return;

    const attribute = employee?.attributes.find(
      (a: SettingsAttribute) => a.attributeDefinitionId === changedAttr.attributeDefinitionId
    );
    if (attribute) {
      employee?.updateAttribute(changedAttr);
    }

    this.setState({ employee, editAttribute: null, actionTarget: null, actionAttribute: null });
  }

  onCancelAddAttribute(_e: SyntheticEvent): void {
    this.setState({ editAttribute: null });
  }

  onAttributeRequirementChange(
    attribute: SettingsAttribute,
    requirementType: RequirementType
  ): void {
    const { employee } = this.state;

    attribute.requirementType = requirementType;
    //employee.attributes.update(attribute);

    this.setState({ employee });
  }

  onAttributesAdded(addedAttributes: SettingsAttribute[]): void {
    const { organization } = this.props;
    const { employee } = this.state;
    if (!employee) return;

    const childAttrs: SettingsAttribute[] = [];
    addedAttributes.forEach((a: SettingsAttribute) => {
      a.childAttributes.forEach((c: SettingsAttribute) => {
        childAttrs.push(c);
        if (
          !addedAttributes.some(
            (attr: SettingsAttribute) => attr.attributeDefinitionId === c.attributeDefinitionId
          ) &&
          !employee.attributes.some(
            (attr: SettingsAttribute) => attr.attributeDefinitionId === c.attributeDefinitionId
          )
        ) {
          const stdAttr = c.toStandardAttribute();
          stdAttr.isNew = true;
          if (
            (stdAttr.hasProficiency && organization && organization.showProficiency) ||
            stdAttr.hasExpiryDate
          )
            stdAttr.validationRequestState = AbstractAttribute.status.feedback;
          else stdAttr.validationRequestState = AbstractAttribute.status.approved;
          addedAttributes.push(stdAttr);
        }
      });

      //Todo: update with default comments, helf off until 1.1
      //a.updateNeedsFeedback();
      //a.comment =
      a.isNew = true;
      if ((a.hasProficiency && organization && organization.showProficiency) || a.hasExpiryDate)
        a.validationRequestState = AbstractAttribute.status.feedback;
      else a.validationRequestState = AbstractAttribute.status.approved;
    });

    employee.attributes = [...employee.attributes, ...addedAttributes, ...childAttrs];

    //employee.addAttribute(addedAttributes);
    this.setState({ employee, showAttributeSelectDialog: false });
  }

  onTrainingsAdded(training: EmployeeTraining): void {
    const { employee } = this.state;
    if (!employee) return;

    employee.trainings = [...employee.trainings, training];

    this.setState({ employee, showAddEditTrainingDialog: false });
  }
  onEditedTraining(training: EmployeeTraining): void {
    const { employee } = this.state;
    if (!employee) return;

    employee.trainings = employee.trainings.map((t: EmployeeTraining) =>
      t.id === training.id ? training : t
    );

    this.setState({ employee, showAddEditTrainingDialog: false });
  }

  onCloseAttributeSelectDialog(): void {
    this.setState({ showAttributeSelectDialog: false });
  }
  onCloseTrainingSelectDialog(): void {
    this.setState({ showAddEditTrainingDialog: false, editTraining: null });
  }

  onCancelSelectVacancy(_e: SyntheticEvent): void {
    const { employee } = this.state;
    if (!employee) return;
    employee.job = (employee as SettingsEmployee & { prevJob?: EmployeeJob }).prevJob;
    this.setState({ showSelectVacancyDialog: false, employee, selectedPositionVacancies: [] });
  }

  onTabChange(_e: SyntheticEvent, newStep: number): void {
    this.setState({ step: newStep, error: null });
  }

  onReportsToClick(_e: SyntheticEvent): void {
    const { employee } = this.state;
    if (!employee || !employee.job) return;
    this.setState({ showAssignManagerDialog: true });
  }

  onSelectVacancy(_e: SyntheticEvent, selectedVacancy: PositionVacancy): void {
    const { employee } = this.state;
    if (!employee) return;

    if (selectedVacancy.isCreatable) employee.manager = undefined;
    else {
      if (employee.job) employee.job.jobId = selectedVacancy.jobId;
      if (selectedVacancy.manager) {
        employee.manager = selectedVacancy.manager as unknown as SettingsEmployee;
        employee.reportsTo = selectedVacancy.manager.jobId;
      }
    }

    this.setState({
      employee,
      showSelectVacancyDialog: false,
    });
  }

  onSaveAssignedManager(
    _e: SyntheticEvent,
    _position: EmployeeJob | null,
    manager: PositionManager | null
  ): void {
    //if (!manager) return;

    const { employee } = this.state;
    if (!employee) return;
    employee.manager = manager as unknown as SettingsEmployee;
    employee.reportsTo = manager ? manager.jobId : undefined;
    // Changed again after returning it to the directory: saving now sets it in Klayo again.
    const returnedToDirectory = this.state.returnedToDirectory?.filter(
      (field) => field !== DirectoryField.Manager
    );
    if (returnedToDirectory?.length !== this.state.returnedToDirectory?.length)
      this.setState({ returnedToDirectory });

    //Don't do this any more: If no manager, change any 'review' attrs
    /*if (!employee.hasManager()) {
            employee.attributes.forEach(a => {
                if (a.validationRequestState === AbstractAttribute.status.review) a.validationRequestState = AbstractAttribute.status.approved;
            });
        }*/

    this.setState({ employee, showAssignManagerDialog: false });
  }

  onCancelAssignedManager(_e: SyntheticEvent): void {
    this.setState({ showAssignManagerDialog: false });
  }

  onBack(): void {
    const { step } = this.state;

    this.setState({ step: step > 0 ? step - 1 : 0 });
  }

  onNavigation(e: SyntheticEvent, callback: (e: SyntheticEvent) => void): void {
    const { onNavigation } = this.props;
    onNavigation && onNavigation(e, callback);
  }

  onCancel(e: SyntheticEvent, _source?: string): void {
    this.onNavigation(e, this.stopBlockingNavAndClose.bind(this));
  }

  stopBlockingNavAndClose(e: SyntheticEvent): void {
    const { onCancel, onBlockNavigation } = this.props;
    if (onBlockNavigation) onBlockNavigation(false, 'Employee');
    if (onCancel) onCancel(e);
  }

  onSave(): void {
    const { onSave, onBlockNavigation, editMode, organization } = this.props;
    const { employee, step } = this.state;
    const hasTraining = !!organization?.enabledFeatures?.Training;

    if (employee) {
      // Via updateName so fullName is recomputed from the trimmed parts.
      employee.updateName(employee.firstName?.trim(), employee.lastName?.trim());
      employee.middleName = employee.middleName?.trim();
      employee.preferredName = employee.preferredName?.trim();
    }

    if (!editMode && step === 0 && this.isValid(employee, 0))
      this.setState({ step: 1, error: null });
    if (!editMode && step === 1 && this.isValid(employee, 1) && hasTraining)
      this.setState({ step: 2, error: null });
    if (onSave && employee && (editMode || step === 2 || (!hasTraining && step === 1))) {
      if (onBlockNavigation) onBlockNavigation(false, 'Employee');
      this.setState({ saving: true });
      onSave(employee, () => this.setState({ saving: false }));
    }
  }

  onEditStateChange(hasEdit: boolean): void {
    this.setState({ hasEdit });
  }

  onLoadPreviousAttributeActivity(
    attribute: SettingsAttribute,
    callback: (attr: SettingsAttribute) => void
  ): void {
    const { onLoadPreviousAttributeActivity } = this.props;
    const { employee } = this.state;
    if (!employee) return;

    if (onLoadPreviousAttributeActivity)
      onLoadPreviousAttributeActivity(attribute, (loadedAttr: SettingsAttribute) => {
        employee.updateAttribute(cloneDeep(loadedAttr));
        this.existingEmployee.updateAttribute(cloneDeep(loadedAttr));
        callback(attribute);
      });
  }

  render() {
    const {
      organization,
      integration,
      theme,
      user,
      className,
      managers,
      editMode,
      onCancel,
      onCreate,
      onShowNewAttributeDialog,
      locations,
      attributes,
      onLoadAttributes,
      onLoadManagers,
      onDocumentClick,
      onAllowNavigation,
      onBlockNavigation,
      onNavigation,
      integrations,
    } = this.props;
    const {
      sameIdEnable,
      error,
      employee,
      jobs,
      employeeLoaded,
      step,
      actionTarget,
      actionAttribute,
      saving,
      showSelectVacancyDialog,
      selectedPositionVacancies,
      showAssignManagerDialog,
      selectedVacancy,
      search,
      searchTraining,
      showAttributeSelectDialog,
      editAttribute,
      hasEdit,
      showAddEditTrainingDialog,
      actionTraining,
      editTraining,
      isSavingEmployee,
      showLimitedUpgradeDialog,
      limitedUpgradeDialogMessage,
    } = this.state;

    const isLimited = !this.state.employee?.isFullAccess;
    // Set by the organization's directory: changing it here sets it in Klayo for this person, and the directory then
    // leaves it alone until it is returned (the API records the override when the employee is saved).
    const overrides = this.state.employee?.directoryOverrides ?? [];
    const overridden = (field: DirectoryField) => overrides.some((o) => o.field === field);
    const jobFromDirectory =
      !!this.state.employee?.isJobManagedByDirectory && !overridden(DirectoryField.JobTitle);
    const locationFromDirectory =
      !!this.state.employee?.isLocationManagedByDirectory && !overridden(DirectoryField.Location);
    const managerFromDirectory =
      !!this.state.employee?.isManagerManagedByDirectory && !overridden(DirectoryField.Manager);
    // Unless the organization keeps them in its directory only: then they are read-only here.
    const directoryOnly = this.state.employee?.canOverrideDirectory === false;
    const directoryHint = this.props.translate(
      directoryOnly ? 'employees.setInDirectory' : 'employees.managedByDirectory'
    );

    let manager = null;
    //if (selectedVacancy && selectedVacancy.manager) manager = selectedVacancy.manager;
    if (employee && employee.manager) manager = employee.manager;

    const filterItems = [{ id: 'search', field: 'attribute', operator: 'contains', value: search }];

    return (
      <EditTemplate
        theme={theme}
        name='Employee'
        onAllowNavigation={onAllowNavigation}
        onBlockNavigation={onBlockNavigation}
        onEditStateChange={this.onEditStateChange.bind(this)}
        detectEdit={!saving && !editAttribute}
        unsavedChangesTitle={this.props.translate('common.unsavedChangesTitle')}
        unsavedChangesQuestion={this.props.translate('common.unsavedChangesQuestion')}
        unsavedChangesAcceptButton={this.props.translate('common.leavePage')}
        unsavedChangesCancelButton={this.props.translate('common.cancel')}
        compare={{
          existing: this.existingEmployee,
          editing: employee,
          members: [
            { name: 'firstName' },
            { name: 'middleName' },
            { name: 'lastName' },
            { name: 'preferredName' },
            { name: 'dateOfBirth', detectEdit: 'compareDay' },
            { name: 'startDate', detectEdit: 'compareDay' },
            { name: 'email' },
            { name: 'employeeNumber' },
            { name: 'locationId' },
            {
              name: 'integrationUsers',
              detectEdit: (
                existing: SettingsEmployee,
                editing: SettingsEmployee,
                isNew?: boolean,
                _isCleared?: boolean
              ) => {
                if (isNew) return Boolean(editing.integrationUsers);

                if (existing.integrationUsers && editing.integrationUsers) {
                  let isChange = false;

                  if (existing.integrationUsers.length !== editing.integrationUsers.length)
                    return !isChange;

                  existing.integrationUsers.forEach((item: IntegrationUser, index: number) => {
                    if (item.username !== editing.integrationUsers[index].username) {
                      isChange = true;
                    }

                    if (
                      item.isForAllIntegrations !==
                      editing.integrationUsers[index].isForAllIntegrations
                    ) {
                      isChange = true;
                    }

                    if (
                      item.organizationIntegrationId !==
                      editing.integrationUsers[index].organizationIntegrationId
                    ) {
                      isChange = true;
                    }
                  });

                  return isChange;
                }
                return false;
              },
            },
            {
              name: 'job',
              detectEdit: (
                existing: SettingsEmployee,
                editing: SettingsEmployee,
                isNew?: boolean,
                isCleared?: boolean
              ) => {
                if (isNew) return Boolean(editing.job);
                if (isCleared) return true;
                if (existing.job && editing.job)
                  return existing.job.jobDefinitionId !== editing.job.jobDefinitionId;
                return false;
              },
            },
            {
              name: 'manager',
              detectEdit: (
                existing: SettingsEmployee,
                editing: SettingsEmployee,
                isNew?: boolean,
                isCleared?: boolean
              ) => {
                if (isNew) return Boolean(editing.manager);
                if (isCleared) return true;
                if (existing.manager && editing.manager)
                  return existing.manager.employeeId !== editing.manager.employeeId;
                return false;
              },
            },
            {
              name: 'attributes',
              detectEdit: (existing: SettingsEmployee, editing: SettingsEmployee) => {
                //return !editing.isAttributesSame(existing.attributes, (i1, i2) => i1.attributeId === i2.attributeId && !i1.isChild && i1.originalValidationRequestState && i2.originalValidationRequestState && i1.originalValidationRequestState.index === i2.originalValidationRequestState.index);
                return !editing.isAttributesSame(
                  existing.attributes,
                  (i1: SettingsAttribute, i2: SettingsAttribute) =>
                    i1.attributeId === i2.attributeId && !i1.isChild
                );
              },
            },
            {
              name: 'trainings',
              detectEdit: (existing: SettingsEmployee, editing: SettingsEmployee) => {
                return !editing.isTrainingsSame(existing.trainings);
              },
            },
          ],
        }}
      >
        <StyledAddEditEmployeeSetting className={`${className ? className : ''}`}>
          <PaperOrDialog
            theme={theme}
            dialogToBreakpoint='md'
            disabledToBreakpoint='md'
            paperPadding={{ xs: '56px 64px' }}
            title={
              editMode
                ? this.props.translate('addEditEmployee.employeeDetails')
                : this.props.translate('addEditEmployee.createEmployee')
            }
            titleSx={{ fontSize: { xs: '20px', md: '30px' }, fontWeight: 'bold' }}
            titleSize={1}
            backButton={editMode}
            onBack={this.onCancel.bind(this)}
          >
            <Stack direction='column' spacing={4}>
              {error && (
                <Alert severity='error' sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}

              {!editMode ? (
                <Stepper activeStep={step}>
                  <Step
                    onClick={this.onBack.bind(this)}
                    sx={{ cursor: step !== 0 ? 'pointer' : 'default' }}
                  >
                    <StepLabel>
                      {(this.props as any).translate('addEditEmployee.employeeInformation')}
                    </StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>
                      {(this.props as any).translate('addEditEmployee.employeeCompetencies')}
                    </StepLabel>
                  </Step>
                  {!!organization?.enabledFeatures?.Training && (
                    <Step>
                      <StepLabel>
                        {(this.props as any).translate('addEditEmployee.employeeTrainings')}
                      </StepLabel>
                    </Step>
                  )}
                </Stepper>
              ) : (
                <div>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      centered={true}
                      value={step}
                      onChange={this.onTabChange.bind(this)}
                      variant='fullWidth'
                      sx={{ width: '100%' }}
                    >
                      <Tab
                        label={(this.props as any).translate('addEditEmployee.information')}
                        value={0}
                      />
                      <Tab
                        label={(this.props as any).translate('addEditEmployee.competencies')}
                        value={1}
                      />
                      {!!organization?.enabledFeatures?.Training && (
                        <Tab
                          label={(this.props as any).translate('addEditEmployee.training')}
                          value={2}
                        />
                      )}
                    </Tabs>
                  </Box>
                </div>
              )}

              {step === 0 && (
                <Fade className='klayo__add-edit-employee-setting-container' in={true}>
                  <div>
                    <Stack direction='column' spacing={4}>
                      <div className='klayo__add-edit-employee-personal-information'>
                        <h2>
                          {(this.props as any).translate('addEditEmployee.personalInformation')}
                        </h2>
                        <Grid container spacing={5}>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate(
                                'addEditEmployee.firstNameRequired'
                              )}
                              value={employee ? employee.firstName : null}
                              clearable={false}
                              autoComplete={false}
                              onChange={this.onFirstNameChange.bind(this)}
                              validationMethod={(_e?: SyntheticEvent, _value?: string) =>
                                this.isFirstNameValid(employee)
                              }
                              validationText={(this.props as any).translate(
                                'addEditEmployee.validFirstNameRequired'
                              )}
                              disabled={saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate('addEditEmployee.middleName')}
                              value={employee ? employee.middleName : null}
                              clearable={false}
                              autoComplete={false}
                              onChange={this.onMiddleNameChange.bind(this)}
                              disabled={isLimited || saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate(
                                'addEditEmployee.lastNameRequired'
                              )}
                              value={employee ? employee.lastName : null}
                              clearable={false}
                              autoComplete={false}
                              onChange={this.onLastNameChange.bind(this)}
                              validationMethod={(_e?: SyntheticEvent, _value?: string) =>
                                this.isLastNameValid(employee)
                              }
                              validationText={(this.props as any).translate(
                                'addEditEmployee.validLastNameRequired'
                              )}
                              disabled={saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate('addEditEmployee.preferredName')}
                              value={employee ? employee.preferredName : null}
                              clearable={false}
                              autoComplete={false}
                              onChange={this.onPreferredNameChange.bind(this)}
                              disabled={isLimited || saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <DatePicker
                              theme={theme}
                              label={(this.props as any).translate('addEditEmployee.dateOfBirth')}
                              allowFuture={false}
                              onChange={this.onDOBChange.bind(this)}
                              value={
                                employee && employee.dateOfBirth
                                  ? new Date(employee.dateOfBirth)
                                  : null
                              }
                              allowEmpty={true}
                              disabled={isLimited || saving}
                              loading={!employeeLoaded}
                              maxDate={new Date()}
                            />
                          </Grid>
                        </Grid>
                      </div>
                      <div className='klayo__add-edit-employee-divider'>
                        <Divider />
                      </div>
                      <div className='klayo__add-edit-employee-information'>
                        <h2>
                          {(this.props as any).translate('addEditEmployee.employeeInformation')}
                        </h2>
                        <Grid container spacing={5}>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate('addEditEmployee.emailRequired')}
                              value={employee ? employee.email : null}
                              clearable={false}
                              autoComplete={false}
                              onChange={this.onEmailChange.bind(this)}
                              validationMethod={(_e?: SyntheticEvent, _value?: string) =>
                                this.isEmailValid(employee)
                              }
                              validationText={(this.props as any).translate(
                                'addEditEmployee.validEmailRequired'
                              )}
                              disabled={saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <LocationSelector<UserLocation>
                              label={this.props.translate('labels.location')}
                              value={employee && employee.location ? employee.location : null}
                              items={locations ? locations : null}
                              callApi={false}
                              multiple={false}
                              compareItems={(a, b) => a.locationId === b.locationId}
                              getItemLabel={(location) => location.name}
                              onChange={this.onLocationsChange.bind(this)}
                              disabled={
                                isLimited || saving || (directoryOnly && locationFromDirectory)
                              }
                              loading={!employeeLoaded || !locations}
                              translate={this.props.translate}
                            />
                            {locationFromDirectory && (
                              <FormHelperText>{directoryHint}</FormHelperText>
                            )}
                            {this.renderDirectoryOverride(DirectoryField.Location)}
                          </Grid>
                          <Grid item xs={6}>
                            <JobSelector
                              label={this.props.translate('labels.job')}
                              items={jobs ? jobs.jobs : null}
                              value={(employee ? employee.job : null) as any}
                              getItemLabel={(job: EmployeeJob) => job.name ?? ''}
                              compareItems={(a: EmployeeJob, b: EmployeeJob) =>
                                a.jobDefinitionId === b.jobDefinitionId
                              }
                              callApi={false}
                              multiple={false}
                              clearable={true}
                              onChange={this.onJobChange.bind(this) as any}
                              onInputChange={this.onJobInputChange.bind(this)}
                              disabled={isLimited || saving || (directoryOnly && jobFromDirectory)}
                              loading={!employeeLoaded || !jobs}
                              translate={this.props.translate}
                            />
                            {jobFromDirectory && <FormHelperText>{directoryHint}</FormHelperText>}
                            {this.renderDirectoryOverride(DirectoryField.JobTitle)}
                          </Grid>
                          {this.renderDirectoryOverride(DirectoryField.Department) && (
                            <Grid item xs={12}>
                              {this.renderDirectoryOverride(DirectoryField.Department)}
                            </Grid>
                          )}
                          <Grid item xs={6}>
                            <Stack direction='row' spacing={1} sx={{ width: '100%' }}>
                              <TextField
                                className='klayo-addeditemployee_reportsto'
                                label={(this.props as any).translate('addEditEmployee.reportsTo')}
                                clearable={false}
                                value={
                                  manager
                                    ? manager.fullName
                                    : (this.props as any).translate('addEditEmployee.unassigned')
                                }
                                disabled={
                                  isLimited ||
                                  !employee ||
                                  !employee.job ||
                                  saving ||
                                  (directoryOnly && managerFromDirectory)
                                }
                                readOnly={true}
                                loading={!employeeLoaded || !employee}
                                onClick={this.onReportsToClick.bind(this)}
                                sx={{ width: '100%' }}
                              />
                              {/* <TooltipIcon
                                                        text='Notifications will go out to both the employee and their manager'
                                                        sx={{ marginTop: '23px!important' }} /> */}
                            </Stack>
                            {managerFromDirectory && (
                              <FormHelperText>{directoryHint}</FormHelperText>
                            )}
                            {this.renderDirectoryOverride(DirectoryField.Manager)}
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={(this.props as any).translate('addEditEmployee.employeeId')}
                              clearable={false}
                              onChange={this.onEmployeeIdChange.bind(this)}
                              value={employee ? employee.employeeNumber : null}
                              disabled={isLimited || saving}
                              loading={!employeeLoaded}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <DatePicker
                              theme={theme}
                              label={(this.props as any).translate('addEditEmployee.startDate')}
                              onChange={this.onStartDateChange.bind(this)}
                              value={
                                employee && employee.startDate ? new Date(employee.startDate) : null
                              }
                              allowEmpty={true}
                              disabled={isLimited || saving}
                              loading={!employeeLoaded}
                            />
                          </Grid>
                          {organization && organization.hasIntegration && (
                            <>
                              <Grid item xs={6}>
                                <Stack spacing={1} sx={{ width: '100%' }}>
                                  {sameIdEnable ? (
                                    // student id handle for sameId state with required
                                    <TextField
                                      label={this.props.translate('addEditEmployee.studentId')}
                                      revalidate={sameIdEnable}
                                      clearable={false}
                                      onChange={this.onStudentIdChange.bind(this)}
                                      value={
                                        employee &&
                                        employee.integrationUsers &&
                                        employee.integrationUsers.length > 0
                                          ? employee.integrationUsers[0].username
                                          : null
                                      }
                                      disabled={isLimited || saving}
                                      validationMethod={(_e?: SyntheticEvent, _value?: string) =>
                                        this.isStudentIdValid(employee)
                                      }
                                      validationText={this.props.translate(
                                        'addEditEmployee.validStudentIdRequired'
                                      )}
                                      loading={!employeeLoaded}
                                      sx={{ width: '100%' }}
                                    />
                                  ) : (
                                    // multi student id not required
                                    <MultiStudentIdTextField
                                      employee={employee}
                                      organization={organization}
                                      integration={integration}
                                      saving={isLimited || saving}
                                      employeeLoaded={employeeLoaded}
                                      onStudentIdChange={this.onStudentIdChange.bind(this)}
                                      isStudentIdValid={this.isStudentIdValid.bind(this)}
                                    />
                                  )}
                                </Stack>
                              </Grid>
                              {organization.hasMultipleIntegrations && (
                                <Grid item xs={6}>
                                  <SameIdSwitchSectionStyled
                                    direction='row'
                                    spacing={1}
                                    justifyContent='space-between'
                                    alignItems='center'
                                    sx={{ width: '100%', maxWidth: '366px', marginTop: '24px' }}
                                  >
                                    <Switch
                                      label={(this.props as any).translate(
                                        'addEditEmployee.sameIdForAllProviders'
                                      )}
                                      checked={sameIdEnable}
                                      onChange={this.onSameIdEnable.bind(this)}
                                      disabled={isLimited}
                                    />
                                    <TooltipIcon
                                      text={(this.props as any).translate(
                                        'addEditEmployee.sameIdTooltip'
                                      )}
                                      placement='right-end'
                                    />
                                  </SameIdSwitchSectionStyled>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </div>
                    </Stack>
                  </div>
                </Fade>
              )}

              {step === 1 && (
                <Fade in={true}>
                  <div>
                    <Stack direction='column' spacing={2}>
                      <div>
                        <Stack
                          direction='row'
                          justifyContent='space-between'
                          alignItems='center'
                          spacing={2}
                        >
                          <TextField
                            value={search}
                            dense={true}
                            placeholder={(this.props as any).translate(
                              'addEditEmployee.searchCompetency'
                            )}
                            disabled={
                              saving ||
                              (employee &&
                                employee.attributes &&
                                employee.attributes.length === 0) ||
                              !employeeLoaded
                            }
                            autoComplete={false}
                            leadingIcon={<SearchIcon />}
                            onChange={this.onSearch.bind(this)}
                            sx={{ minWidth: '300px' }}
                          />

                          <Button
                            onClick={this.onShowAttributeSelectDialog.bind(this)}
                            size='md'
                            variant='outlined'
                            startIcon={<AddIcon />}
                            label={(this.props as any).translate('addEditEmployee.addCompetencies')}
                            disabled={saving || !employeeLoaded || !locations}
                          />
                        </Stack>
                      </div>
                      <div>
                        <AttributeTable
                          // rows={employee ? employee.attributes : null}
                          rows={
                            (this.state.search
                              ? this.state.filteredAttributes
                              : employee?.attributes) as any
                          }
                          pagination={false}
                          showFooter={false}
                          theme={theme}
                          translate={(this.props as any).translate}
                          filterItems={filterItems}
                          dense={true}
                          tree={true}
                          onAttributeAction={this.onAttributeAction.bind(this) as any}
                          onAttributeSelect={this.onAttributeSelect.bind(this) as any}
                          onRequirementChange={this.onAttributeRequirementChange.bind(this) as any}
                          paper={false}
                          disabled={saving}
                          rowHasAction={((attr: SettingsAttribute) => attr.isChild !== true) as any}
                          childRowClass={
                            ((attr: SettingsAttribute) => {
                              return (
                                'klayo-attributetable_nonactionablechild' +
                                (employee &&
                                employee.attributes &&
                                !employee.attributes.some(
                                  (a: SettingsAttribute) =>
                                    a.attributeDefinitionId === attr.attributeDefinitionId &&
                                    !a.isChild
                                )
                                  ? ' klayo-attributetable_ghosted'
                                  : '')
                              );
                            }) as any
                          }
                          columns={[
                            {
                              type: AttributeTable.columns.attribute,
                              label: (this.props as any).translate('tableColumns.competency'),
                            },
                            //{ type: AttributeTable.columns.comment },
                            //{ type: AttributeTable.columns.currentProficiency },
                            //{ type: AttributeTable.columns.documents, label: 'Docs' },
                            //{ type: AttributeTable.columns.expiryTest },
                            //{ type: AttributeTable.columns.validationRequestStateTest },
                            {
                              type: AttributeTable.columns.reasonLong,
                              label: (this.props as any).translate('tableColumns.status'),
                            },
                            //{ type: AttributeTable.columns.needsFeedback },
                            { type: AttributeTable.columns.attachmentIcon },
                            { type: AttributeTable.columns.actions },
                          ]}
                          noRowsComponent={
                            <TablePlaceholder
                              text={(this.props as any).translate(
                                'addEditEmployee.useAddCompetenciesButton'
                              )}
                              icon={<AttributesIcon />}
                            />
                          }
                          keyword={search ?? undefined}
                        />

                        {actionAttribute && (
                          <Menu
                            open={actionTarget !== null}
                            anchorEl={actionTarget}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            onClose={this.onCloseActionMenu.bind(this)}
                          >
                            {!actionAttribute.isParent && (
                              <MenuItem onClick={this.onEditAttribute.bind(this)}>
                                <ListItemText>
                                  {(this.props as any).translate('addEditEmployee.editCompetency')}
                                </ListItemText>
                              </MenuItem>
                            )}
                            <MenuItem onClick={this.onRemoveAttribute.bind(this)}>
                              <ListItemText>
                                {(this.props as any).translate('addEditEmployee.removeCompetency')}
                              </ListItemText>
                            </MenuItem>
                          </Menu>
                        )}
                      </div>
                    </Stack>
                  </div>
                </Fade>
              )}
              {step === 2 && !!organization?.enabledFeatures?.Training && (
                <Fade in={true}>
                  <div>
                    <Stack direction='column' spacing={2}>
                      <div>
                        <Stack
                          direction='row'
                          justifyContent='space-between'
                          alignItems='center'
                          spacing={2}
                        >
                          <TextField
                            value={searchTraining}
                            dense={true}
                            placeholder={(this.props as any).translate(
                              'addEditEmployee.searchTraining'
                            )}
                            disabled={
                              saving ||
                              (employee && employee.trainings && employee.trainings.length === 0) ||
                              !employeeLoaded
                            }
                            autoComplete={false}
                            leadingIcon={<SearchIcon />}
                            onChange={this.onSearchTrainings.bind(this)}
                            sx={{ minWidth: '300px' }}
                          />

                          <Button
                            onClick={this.onShowAddEditTrainingDialog.bind(this)}
                            size='md'
                            variant='outlined'
                            startIcon={<AddIcon />}
                            label={(this.props as any).translate('addEditEmployee.addTraining')}
                            disabled={saving || !employeeLoaded || !locations}
                          />
                        </Stack>
                      </div>
                      <div>
                        <ProfileTrainingTable
                          rows={
                            (this.state.searchTraining
                              ? this.state.filteredTrainings
                              : employee?.trainings) as any
                          }
                          columns={[
                            { type: ProfileTrainingTable.columns.completedDate },
                            { type: ProfileTrainingTable.columns.name },
                            { type: ProfileTrainingTable.columns.status },
                            { type: ProfileTrainingTable.columns.attachmentIcon },
                            {
                              type: ProfileTrainingTable.columns.moreActions,
                            },
                          ]}
                          theme={theme}
                          translate={(this.props as any).translate}
                          sortable={true}
                          sortingMode='client'
                          selectable={false}
                          paper={false}
                          onRowSelect={this.onTrainingSelect.bind(this) as any}
                          rowHasAction={((row: EmployeeTraining) => row.hasActions) as any}
                          rowHasSelect={((row: EmployeeTraining) => row.hasActions) as any}
                          onRowAction={this.onTrainingAction.bind(this) as any}
                          noRowsComponent={
                            <TablePlaceholder
                              text={(this.props as any).translate(
                                'addEditEmployee.useAddTrainingButton'
                              )}
                              icon={<AttributesIcon />}
                            />
                          }
                          keyword={searchTraining ?? undefined}
                        />

                        {actionTraining && (
                          <Menu
                            open={actionTarget !== null}
                            anchorEl={actionTarget}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            onClose={this.onCloseActionMenu.bind(this)}
                          >
                            <MenuItem onClick={this.onEditTraining.bind(this)}>
                              <ListItemText>
                                {(this.props as any).translate('addEditEmployee.editTraining')}
                              </ListItemText>
                            </MenuItem>
                            <MenuItem onClick={this.onRemoveTraining.bind(this)}>
                              <ListItemText>
                                {(this.props as any).translate('addEditEmployee.removeTraining')}
                              </ListItemText>
                            </MenuItem>
                          </Menu>
                        )}
                      </div>
                    </Stack>
                  </div>
                </Fade>
              )}

              <StyleEmployeeSettingBottomBtn
                className='klayo__add-edit-employee-bottom-btn-container'
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                spacing={2}
                editMode={editMode ? true : false}
              >
                <Button
                  onClick={this.onCancel.bind(this)}
                  disabled={saving}
                  label={(this.props as any).translate('common.cancel')}
                />
                <Stack direction='row' spacing={2}>
                  {step > 0 && !editMode && (
                    <Button
                      variant='outlined'
                      onClick={this.onBack.bind(this)}
                      startIcon={<LeftIcon />}
                      label={(this.props as any).translate('common.back')}
                    />
                  )}
                  <Button
                    variant='filled'
                    disabled={!this.isValid(employee, step) || !hasEdit || saving}
                    onClick={this.onSave.bind(this)}
                    endIcon={
                      !editMode &&
                      (step === 0 || (step === 1 && !!organization?.enabledFeatures?.Training)) ? (
                        <RightIcon />
                      ) : null
                    }
                    label={
                      editMode
                        ? (this.props as any).translate('common.save')
                        : step === 0 || (step === 1 && !!organization?.enabledFeatures?.Training)
                          ? (this.props as any).translate('common.continue')
                          : (this.props as any).translate('common.create')
                    }
                    isLoading={isSavingEmployee}
                  />
                </Stack>
              </StyleEmployeeSettingBottomBtn>
            </Stack>

            {showSelectVacancyDialog && employee && employee.job && !employee.job.jobId && (
              <SelectVacancyDialog
                {...({
                  position: employee ? employee.job : null,
                  vacancy: selectedVacancy,
                  theme: theme,
                  vacancies: selectedPositionVacancies,
                  onSave: this.onSelectVacancy.bind(this),
                  onCancel: this.onCancelSelectVacancy.bind(this),
                } as any)}
              />
            )}

            {showAttributeSelectDialog && (
              <AttributeSelectorDialog
                {...({
                  attributes: attributes,
                  theme: theme,
                  excludedAttributes: employee
                    ? employee.attributes.filter((a: SettingsAttribute) => !a.isChild)
                    : null,
                  jobDefinitionId:
                    (employee && employee.job ? employee.job.jobDefinitionId : null) ?? undefined,
                  showSuggestions: true,
                  onLoadAttributes: onLoadAttributes,
                  onShowNewAttributeDialog: onShowNewAttributeDialog,
                  onClose: this.onCloseAttributeSelectDialog.bind(this),
                  onSave: this.onAttributesAdded.bind(this),
                } as any)}
              />
            )}
            <AddEditTrainingDialog
              isOpen={showAddEditTrainingDialog}
              theme={theme}
              onClose={this.onCloseTrainingSelectDialog.bind(this) as any}
              onSave={
                !editTraining ? this.onTrainingsAdded.bind(this) : this.onEditedTraining.bind(this)
              }
              employee={employee as any}
              user={user as any}
              mode={!editTraining ? Training_Dialog_Mode.add : Training_Dialog_Mode.edit}
              integrations={integrations as any}
              training={editTraining as any}
            />

            {showAssignManagerDialog && employee && (
              <AssignManagerDialog
                excludeEmployee={employee}
                theme={theme}
                managers={managers}
                position={employee.job}
                manager={manager}
                allowNone={true}
                onLoadManagers={onLoadManagers}
                onSave={this.onSaveAssignedManager.bind(this) as any}
                onCancel={this.onCancelAssignedManager.bind(this) as any}
              />
            )}

            {editAttribute && (
              <EditAttributeDialog
                {...({
                  user: user,
                  employee: user,
                  theme: theme,
                  attribute: editAttribute,
                  onSave: this.onSaveAttribute.bind(this),
                  allowNeedsFeedback: employee,
                  orgHasProficiency: organization && organization.showProficiency,
                  onLoadPreviousAttributeActivity: this.onLoadPreviousAttributeActivity.bind(this),
                  onDocumentClick: onDocumentClick,
                  onAllowNavigation: onAllowNavigation,
                  onBlockNavigation: onBlockNavigation,
                  onNavigation: onNavigation,
                  onCancel: this.onCancelAddAttribute.bind(this),
                } as any)}
              />
            )}
          </PaperOrDialog>

          <Dialog
            open={showLimitedUpgradeDialog}
            theme={theme}
            onClose={() => this.setState({ showLimitedUpgradeDialog: false })}
            fullWidth={true}
            maxWidth='sm'
            title={(this.props as any).translate('addEditEmployee.upgradeRequired')}
            actions={[
              {
                label: (this.props as any).translate('common.upgrade'),
                variant: 'filled',
                onClick: () =>
                  this.props.history.push('/pricing-and-checkout', {
                    from: this.props.history.location.pathname + this.props.history.location.search,
                  }),
              },
              {
                label: (this.props as any).translate('common.close'),
                variant: 'outlined',
                onClick: () => this.setState({ showLimitedUpgradeDialog: false }),
              },
            ]}
          >
            {limitedUpgradeDialogMessage &&
              (this.props as any).translate(limitedUpgradeDialogMessage)}
          </Dialog>
        </StyledAddEditEmployeeSetting>
      </EditTemplate>
    );
  }
}

const StyledAddEditEmployeeSetting = styled.div`
  &.klayo__add-edit-employee-setting {
    .klayo__add-edit-employee-setting-container {
      margin-top: 40px;

      h2 {
        margin-bottom: 40px;
      }

      .klayo__add-edit-employee-personal-information {
        .MuiGrid-item {
          padding-top: 44px;
        }
      }

      .klayo__add-edit-employee-divider {
        margin-top: 40px;
      }

      .klayo__add-edit-employee-information {
        margin-top: 40px;

        .MuiGrid-item {
          padding-top: 44px;
        }
      }
    }

    .klayo__add-edit-employee-bottom-btn-container {
      margin-top: 40px;
    }
  }
`;

const StyleEmployeeSettingBottomBtn = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'editMode',
})<{ editMode?: boolean }>`
  &.klayo__add-edit-employee-bottom-btn-container {
    justify-content: ${(props) => (props.editMode ? 'start' : 'space-between')};
  }
`;

const SameIdSwitchSectionStyled = styled(Stack)`
  .MuiFormControlLabel-root {
    margin-right: 0px;
  }

  .MuiBox-root {
  }
`;

export const AddEditEmployeeView = withLocale(AddEditEmployeeViewBase);
